<template>
  <div class="address-page">
    <div class="page-container">
      <!-- 面包屑 -->
      <nav class="breadcrumb">
        <a class="bc-link" @click.prevent="goHome">首页</a>
        <span class="bc-sep">/</span>
        <span class="bc-current">我的地址</span>
      </nav>

      <!-- Hero -->
      <div class="hero-row">
        <div class="hero-left">
          <p class="hero-brand">关盈堂 · 收货地址</p>
          <h1 class="hero-title">地址清晰，配送更顺畅</h1>
          <p class="hero-subtitle">维护常用收件信息，让每一次配送与签收都更省心。</p>
        </div>
        <button class="hero-add" type="button" @click="showAddDialog = true">
          <span class="hero-add-plus" aria-hidden="true">＋</span>
          <span>添加地址</span>
        </button>
      </div>

      <!-- 提示条 -->
      <div class="notice-bar">
        <span class="notice-icon" aria-hidden="true">!</span>
        <p class="notice-text">地址信息仅用于订单配送与签收，请填写真实有效的收件人与联系方式。</p>
      </div>

      <!-- 地址卡 -->
      <div class="panel-card">
        <p class="panel-eyebrow">ADDRESS BOOK</p>
        <div class="panel-head">
          <h2 class="panel-title">我的地址</h2>
          <span class="panel-badge">{{ addressTotal }} 条</span>
        </div>

        <!-- 查找地址 -->
        <div class="search-block">
          <label class="search-label">查找地址</label>
          <el-input class="address-search" v-model="searchText" placeholder="收件人姓名或手机号码" clearable
            @keyup.enter.native="getAddressList">
            <i slot="suffix" class="el-input__icon el-icon-search search-icon" @click="getAddressList"></i>
          </el-input>
        </div>

        <!-- 地址列表 -->
        <div class="address-list" v-if="addressList.length > 0">
          <div class="address-row" v-for="(row, index) in addressList" :key="row.deliveryAddressId || index">
            <div class="ar-profile">
              <span class="ar-avatar">{{ avatarText(row.receiverName) }}</span>
              <div class="ar-profile-info">
                <span class="ar-name">{{ row.receiverName }}</span>
                <span class="ar-sub">{{ row.phoneNo || '未填写电话' }}</span>
              </div>
            </div>

            <div class="ar-cell">
              <span class="ar-label">省市区</span>
              <span class="ar-value">{{ row.addressInfoText || '未填写' }}</span>
            </div>
            <div class="ar-cell">
              <span class="ar-label">详细地址</span>
              <span class="ar-value">{{ row.street || '未填写' }}</span>
            </div>
            <div class="ar-cell ar-cell-zip">
              <span class="ar-label">邮编</span>
              <span class="ar-value">{{ row.zipCode || '未填写' }}</span>
            </div>

            <div class="ar-action">
              <button v-if="isSelect" type="button" class="ar-btn ar-btn-select" @click="selectAddress(row)">
                <i class="el-icon-circle-check"></i> 选择
              </button>
              <button type="button" class="ar-btn" @click="editAddress(row)">
                <i class="el-icon-edit"></i> 编辑
              </button>
              <button type="button" class="ar-btn ar-btn-delete" @click="deleteAddress(row)">
                <i class="el-icon-delete"></i> 删除
              </button>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div class="address-empty" v-else>
          <p>{{ emptyText }}</p>
        </div>

        <!-- 底部：统计 + 分页 + 新增 -->
        <div class="panel-foot">
          <span class="foot-total">共找到 {{ addressTotal }} 条地址</span>
          <div class="foot-right">
            <el-pagination v-if="totalPage > 10" class="pagination" background layout="prev, pager, next"
              :current-page="currentPageNo" @current-change="pageChange" :total="totalPage" />
            <button class="foot-add" type="button" @click="showAddDialog = true">＋ 新增地址</button>
          </div>
        </div>
      </div>

      <!-- 底部提示 -->
      <div class="page-bottom">
        <p class="bottom-hint">请确保收件信息真实有效，以便配送与签收顺利完成。</p>
        <button class="bottom-back" type="button" @click="goBottomBack">
          <span>{{ isSelect ? '返回上一页' : '返回我的服务' }}</span>
          <span class="bottom-back-arrow" aria-hidden="true">→</span>
        </button>
      </div>
    </div>

    <el-dialog :title="isEdit ? '编辑地址' : '添加地址'" :visible.sync="showAddDialog" center destroy-on-close width="750px"
      custom-class="address-dialog">
      <div style="display: flex;flex-direction: column;">
        <el-form label-width="160px" label-position="right" class="basic-info" :model="deliveryFormData">
          <el-form-item label="*姓名：" class="form-item">
            <el-input v-model="deliveryFormData.receiverName" ref="nameId" type="text" placeholder="请输入收件人姓名"
              clearable></el-input>
          </el-form-item>
          <el-form-item label="*联系电话：" class="form-item">
            <el-input v-model="deliveryFormData.phoneNo" ref="phoneId" type="number" placeholder="请输入联系人手机号码"
              clearable></el-input>
          </el-form-item>
          <el-form-item label="*省市区：" class="form-item">
            <el-cascader style="width: 100%;" placeholder="请选择省市区" @change="addressChange"
              v-model="deliveryFormData.addressInfo" :options="cascaderOptions"></el-cascader>
          </el-form-item>
          <el-form-item label="*详细地址：" class="form-item">
            <el-input v-model="deliveryFormData.street" type="textarea" ref="customAddressId"
              placeholder="请输入详细地址(街道门牌信息)" clearable></el-input>
          </el-form-item>
          <el-form-item label="邮政编码：" class="form-item">
            <el-input v-model="deliveryFormData.zipCode" type="number" placeholder="请输入邮政编码" clearable></el-input>
          </el-form-item>
        </el-form>
        <div class="dialog-actions">
          <el-button class="dialog-btn-cancel" size="medium" @click="dismiss">取 消</el-button>
          <el-button class="dialog-btn-save" type="primary" size="medium" @click="saveAddress">保 存</el-button>
        </div>
      </div>
    </el-dialog>
  </div>

</template>

<script>
import AreaHeader from '@/components/widgets/area-header.vue'
import area from '../../js/area'
import { EventBus } from "../../js/eventbus.js"
export default {
  components: {
    AreaHeader,
  },
  data() {
    return {
      addressList: [],
      addressTotal: 0,
      deliveryFormData: {
        city: "",
        county: "",
        phoneNo: "",
        province: "",
        receiverName: "",
        street: "",
        zipCode: '',
        addressInfo: [],
        addressInfoText: [],
      },
      showAddDialog: false,
      searchText: '',
      addressTemp: '',
      currentPageNo: 1,
      pageSize: 12,
      totalPage: 1,
      emptyText: '您还没有地址',
      isEdit: false,
      isSelect: false,
      cascaderOptions: [],
    }
  },
  created() {
    this.cascaderOptions = this.buildCascaderOptions()
    var mode = this.$route.query.mode
    this.isSelect = mode == 1
    this.getAddressList()
  },
  methods: {
    buildCascaderOptions() {
      // 按行政区编码前缀构建 省→市→区 三级全量选项
      // value 使用中文名，与 addressInfo / 后端保存值保持一致，保证编辑时可正确回显
      var cityByProvince = {}
      for (var cCode in area.city_list) {
        var pPrefix = cCode.substring(0, 2)
        if (!cityByProvince[pPrefix]) cityByProvince[pPrefix] = []
        cityByProvince[pPrefix].push(cCode)
      }
      var countyByCity = {}
      for (var dCode in area.county_list) {
        var cPrefix = dCode.substring(0, 4)
        if (!countyByCity[cPrefix]) countyByCity[cPrefix] = []
        countyByCity[cPrefix].push(dCode)
      }
      var options = []
      for (var pCode in area.province_list) {
        var provinceName = area.province_list[pCode]
        var cityCodes = cityByProvince[pCode.substring(0, 2)] || []
        var cities = []
        for (var i = 0; i < cityCodes.length; i++) {
          var cityCode = cityCodes[i]
          var cityName = area.city_list[cityCode]
          var countyCodes = countyByCity[cityCode.substring(0, 4)] || []
          var counties = []
          for (var j = 0; j < countyCodes.length; j++) {
            var countyName = area.county_list[countyCodes[j]]
            counties.push({ value: countyName, label: countyName })
          }
          cities.push({ value: cityName, label: cityName, children: counties })
        }
        options.push({ value: provinceName, label: provinceName, children: cities })
      }
      return options
    },
    goBack() {
      this.$router.go(-1)
    },
    goHome() {
      this.$router.push('/')
    },
    goBottomBack() {
      // 选择地址模式（mode=1）来自表单流程，优先返回上一页；其它情况回首页
      if (this.isSelect) {
        this.goBack()
      } else {
        this.goHome()
      }
    },
    avatarText(name) {
      var n = name ? String(name) : ''
      return n ? n.charAt(0) : '址'
    },
    getAddressList() {
      this.wjtHttp.get(this, '/api/delivery/address/list', {
        pageNum: this.currentPageNo,
        pageSize: this.pageSize,
        keyWord: this.searchText
      }, true, (res) => {
        var data = res.data
        if (data.code == 200) {
          this.addressTotal = data.total || 0
          if (!this.utils.isEmptyStr(data.rows)) {
            this.totalPage = Math.ceil(data.total / 12) * 10
          } else if (this.currentPageNo == 1) {
            this.totalPage = 1
          }
          for (var i = 0; i < data.rows.length; i++) {
            var item = data.rows[i]
            item.addressInfo = [item.province, item.city, item.county]
            item.addressInfoText = item.province + '/' + item.city + '/' + item.county
          }
          this.addressList = data.rows
        } else {
          this.$message({
            message: (data.msg && data.msg.length > 0) ? data.msg : '获取地址列表异常',
            type: 'error'
          })
        }
      }, (err) => {
        console.log(err)
      })
    },
    dismiss() {
      this.showAddDialog = false
      this.cancelEdit()
    },
    addressChange(e) {
      console.log(e)
      if (e.length >= 3) {
        this.deliveryFormData.province = e[0]
        this.deliveryFormData.city = e[1]
        this.deliveryFormData.county = e[2]
        this.addressTemp = ''
      }
    },
    editAddress(item) {
      console.log(item)
      this.deliveryFormData = {
        receiverName: item.receiverName,
        phoneNo: item.phoneNo,
        province: item.province,
        city: item.city,
        county: item.county,
        street: item.street,
        zipCode: item.zipCode,
        addressInfo: [item.province, item.city, item.county],
        addressInfoText: item.province + '/' + item.city + '/' + item.county,
        deliveryAddressId: item.deliveryAddressId
      }
      this.isEdit = true
      this.showAddDialog = true
      this.addressTemp = this.deliveryFormData.addressInfoText
    },
    deleteAddress(item) {
      this.$confirm('确定删除该地址信息吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.wjtHttp.delete(this, '/api/delivery/address/' + item.deliveryAddressId, {}, true, (res) => {
          var data = res.data
          if (data.code == 200) {
            this.$message({
              type: 'success',
              message: '删除成功!'
            });
            this.getAddressList()
          } else {
            this.$message({
              message: (data.msg && data.msg.length > 0) ? data.msg : '删除地址列表异常',
              type: 'error'
            })
          }
        }, (err) => {
          console.log(err)
        })
      }).catch(() => {

      });

    },
    saveAddress() {
      if (this.deliveryFormData.receiverName.length == 0) {
        this.$nextTick(() => {
          this.$refs.nameId.focus();
        })
        this.$message("请输入收件人姓名")
        return
      }
      if (this.deliveryFormData.phoneNo.length != 11) {
        this.$nextTick(() => {
          this.$refs.phoneId.focus();
        })
        this.$message("请输入正确的11位手机号")
        return
      }
      if (this.deliveryFormData.addressInfo.length == 0) {
        this.$message("请选择省市区")
        return
      }
      if (this.deliveryFormData.street.length == 0) {
        this.$nextTick(() => {
          this.$refs.customAddress.focus();
        })
        this.$message("请输入详细地址")
        return
      }
      this.wjtHttp.post(this, this.isEdit ? '/api/delivery/address/edit' : '/api/delivery/address/add', this.deliveryFormData, true, (res) => {
        var data = res.data
        if (data.code == 200) {
          this.clearAddress()
          this.getAddressList()
          this.showAddDialog = false
        } else {
          this.$message({
            message: (data.msg && data.msg.length > 0) ? data.msg : (this.isEdit ? "编辑地址异常" : '获取地址列表异常'),
            type: 'error'
          })
        }
      }, (err) => {
        console.log(err)
      })
    },
    cancelEdit() {
      this.isEdit = false
      this.clearAddress()
      this.addressTemp = ""
    },
    clearAddress() {
      this.deliveryFormData = {
        city: "",
        county: "",
        phoneNo: "",
        province: "",
        receiverName: "",
        street: "",
        zipCode: '',
        addressInfo: [],
        addressInfoText: '',
        deliveryAddressId: ""
      }
      this.addressTemp = ""
    },
    selectAddress(item) {
      console.log("item:", item)
      sessionStorage.setItem('selectAddress', JSON.stringify(item))
      this.$router.go(-1)
    },
    pageChange(pageNo) {
      this.currentPageNo = pageNo
      this.getAddressList()
    },
  },
  watch: {
    searchText: function (val) {
      if (this.utils.isEmptyStr(val)) {
        this.currentPageNo = 1
        this.getAddressList()
      }
    },
    showAddDialog: function (val) {
      if (!val) {
        this.cancelEdit()
      }
    }
  }
}
</script>

<style scoped>
/* ============================================================
   AddressPage — 我的地址（青绿主题 / 对齐患者档案页）
   ============================================================ */
.address-page {
  min-height: 60vh;
  background: #f5f7f2;
  color: #304744;
  padding-bottom: 20px;
}

.page-container {
  max-width: 1220px;
  margin: 0 auto;
  padding: 32px 24px 40px;
}

/* ========== 面包屑 ========== */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  margin-bottom: 18px;
}

.bc-link {
  color: #527f77;
  cursor: pointer;
}

.bc-link:hover {
  color: #009798;
}

.bc-sep {
  color: #b6c6c1;
}

.bc-current {
  color: #304744;
  font-weight: 600;
}

/* ========== Hero ========== */
.hero-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}

.hero-brand {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 600;
  color: #009798;
  letter-spacing: 1px;
}

.hero-title {
  margin: 0 0 12px;
  font-size: 40px;
  font-weight: 700;
  color: #304744;
  letter-spacing: 1px;
  line-height: 1.2;
}

.hero-subtitle {
  margin: 0;
  font-size: 17px;
  color: #527f77;
}

.hero-add {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 24px;
  border: 1px solid #009798;
  border-radius: 12px;
  background: #009798;
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 10px 22px rgba(0, 151, 152, 0.22);
}

.hero-add:hover {
  background: #007e7f;
  border-color: #007e7f;
  transform: translateY(-2px);
}

.hero-add-plus {
  font-size: 18px;
  line-height: 1;
}

/* ========== 提示条 ========== */
.notice-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 22px;
  margin-bottom: 26px;
  border: 1px solid #f2e4c4;
  border-radius: 14px;
  background: #fdf7e9;
}

.notice-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1.5px solid #c9a24a;
  color: #c9a24a;
  font-size: 16px;
  font-weight: 700;
}

.notice-text {
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  color: #8a6d3b;
}

/* ========== 地址卡 ========== */
.panel-card {
  background: #fff;
  border: 1px solid #e9efec;
  border-radius: 22px;
  padding: 30px 34px 26px;
  box-shadow: 0 10px 30px rgba(23, 61, 59, 0.06);
}

.panel-eyebrow {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #a9bcb6;
}

.panel-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
}

.panel-title {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  color: #304744;
}

.panel-badge {
  padding: 5px 14px;
  border-radius: 10px;
  background: #e6f4f1;
  color: #009798;
  font-size: 16px;
  font-weight: 600;
}

/* 查找地址 */
.search-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 22px;
}

.search-label {
  font-size: 16px;
  color: #7f9994;
}

.address-search {
  max-width: 560px;
}

.address-search>>>.el-input__inner {
  height: 44px;
  border-radius: 12px;
  border-color: #d3e6e3;
  color: #304744;
  font-size: 16px;
}

.address-search>>>.el-input__inner:focus {
  border-color: #009798;
}

.address-search>>>.el-input__suffix {
  display: flex;
  align-items: center;
}

.search-icon {
  color: #009798;
  font-size: 17px;
  cursor: pointer;
}

.search-icon:hover {
  color: #007e7f;
}

/* ========== 地址列表 ========== */
.address-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.address-row {
  display: grid;
  grid-template-columns: 190px 1fr 1.2fr 100px 260px;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  border: 1px solid #e9efec;
  border-radius: 16px;
  background: #fbfdfc;
  transition: all 0.2s ease;
}

.address-row:hover {
  border-color: #b7ddd8;
  background: #fff;
  box-shadow: 0 10px 24px rgba(23, 61, 59, 0.07);
}

.ar-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ar-avatar {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #d9efeb;
  color: #009798;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ar-profile-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.ar-name {
  font-size: 18px;
  font-weight: 700;
  color: #304744;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ar-sub {
  font-size: 16px;
  color: #9aa8a3;
}

.ar-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.ar-label {
  font-size: 16px;
  color: #a9bcb6;
}

.ar-value {
  font-size: 16px;
  font-weight: 600;
  color: #304744;
  word-break: break-all;
}

.ar-action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.ar-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 16px;
  border: 1px solid #b7ddd8;
  border-radius: 10px;
  background: #fff;
  color: #009798;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ar-btn:hover {
  background: #eaf6f4;
  border-color: #009798;
}

.ar-btn-select {
  border-color: #009798;
  background: #009798;
  color: #fff;
}

.ar-btn-select:hover {
  background: #007e7f;
  border-color: #007e7f;
  color: #fff;
}

.ar-btn-delete {
  border-color: #f0d3ce;
  color: #d35342;
}

.ar-btn-delete:hover {
  background: #fdf3f1;
  border-color: #d35342;
  color: #b93c2e;
}

/* 空状态 */
.address-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  border: 1px dashed #dcebe7;
  border-radius: 16px;
  background: #fbfdfc;
}

.address-empty p {
  margin: 0;
  font-size: 17px;
  color: #9aa8a3;
}

/* ========== 卡内底部 ========== */
.panel-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #eef2f0;
}

.foot-total {
  font-size: 16px;
  color: #7f9994;
}

.foot-right {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.pagination {
  margin: 0;
}

.foot-add {
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #009798;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.foot-add:hover {
  background: #eaf6f4;
}

/* ========== 底部提示 ========== */
.page-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 20px;
}

.bottom-hint {
  margin: 0;
  font-size: 16px;
  color: #9aa8a3;
}

.bottom-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  border: 1px solid #cfdcd8;
  border-radius: 12px;
  background: #fff;
  color: #304744;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bottom-back:hover {
  border-color: #009798;
  color: #009798;
}

.bottom-back-arrow {
  color: #009798;
  font-weight: 700;
}

/* ========== 分页配色 ========== */
.pagination>>>.el-pager li.active {
  background-color: #009798 !important;
  color: #fff !important;
}

.pagination>>>.el-pager li:hover {
  color: #009798;
}

.pagination>>>.btn-prev,
.pagination>>>.btn-next {
  background-color: #f1f7f5;
}

/* ========== 对话框（保持原样式） ========== */
.address-dialog {
  border-radius: 14px;
}

.address-dialog>>>.el-dialog__header {
  background: #f4faf9;
  border-radius: 14px 14px 0 0;
  padding: 20px 24px;
  border-bottom: 1px solid #eef1f0;
}

.address-dialog>>>.el-dialog__title {
  font-size: 18px;
  font-weight: 700;
  color: #304744;
}

.address-dialog>>>.el-dialog__body {
  padding: 24px 32px;
}

.basic-info {
  width: 100%;
  margin-top: 10px;
  margin-bottom: 10px;
}

.form-item {
  width: 100%;
}

.basic-info>>>.el-input__inner,
.basic-info>>>.el-textarea__inner {
  border-radius: 10px;
  border-color: #e2e8f0;
}

.basic-info>>>.el-input__inner:focus,
.basic-info>>>.el-textarea__inner:focus {
  border-color: #009798;
}

.dialog-actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 32px;
  margin-top: 10px;
  margin-bottom: 6px;
}

.dialog-btn-save {
  width: 180px;
  border-radius: 12px;
  background-color: #009798 !important;
  border-color: #009798 !important;
  color: #fff !important;
  font-weight: 600;
}

.dialog-btn-save:hover,
.dialog-btn-save:focus {
  background-color: #007e7f !important;
  border-color: #007e7f !important;
}

.dialog-btn-cancel {
  width: 180px;
  border-radius: 12px;
  border-color: #009798;
  color: #009798;
  font-weight: 600;
}

.dialog-btn-cancel:hover {
  background-color: #eaf6f4;
  border-color: #009798;
  color: #009798;
}

/* ========== 响应式 ========== */
@media (max-width: 1280px) {
  .address-row {
    grid-template-columns: 180px 1fr 1fr 240px;
    row-gap: 16px;
  }

  .ar-profile {
    grid-column: span 3;
  }

  .ar-action {
    grid-column: 4;
    grid-row: 1;
    align-self: center;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 20px 16px 32px;
  }

  .hero-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .hero-title {
    font-size: 28px;
  }

  .hero-add {
    width: 100%;
    justify-content: center;
  }

  .panel-card {
    padding: 20px 16px;
    border-radius: 18px;
  }

  .panel-title {
    font-size: 22px;
  }

  .address-search {
    max-width: 100%;
  }

  .address-row {
    grid-template-columns: 1fr;
    row-gap: 14px;
  }

  .ar-profile,
  .ar-action {
    grid-column: 1;
    grid-row: auto;
  }

  .ar-action {
    justify-content: flex-start;
  }

  .panel-foot,
  .page-bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
